import React, { useState, useEffect, useImperativeHandle, forwardRef, useCallback, useMemo } from 'react';
import { FixedSizeGrid as Grid } from 'react-window';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../supabaseClient';

const QuantitySelector = forwardRef(({
  quantity: quantityFromParent,
  onQuantityChange, 
  ticketPrice = 50,
  disabled = false,
  onTicketsSelected = () => {}
}, ref) => {

  const [selectionMode, setSelectionMode] = useState('manual');
  const [activeButton, setActiveButton] = useState(null);
  const [quantity, setQuantity] = useState(quantityFromParent || 2);
  const [selectedTickets, setSelectedTickets] = useState(new Set());
  const [availableTickets, setAvailableTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [verifyingTickets, setVerifyingTickets] = useState(new Set());
  const [gridWidth, setGridWidth] = useState(0);

  // Cache para verificaciones de tickets
  const [verificationCache, setVerificationCache] = useState(new Map());

  const fetchAvailableTickets = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("🚀 Iniciando consulta a la tabla 'raffles'...");

      const { data: raffleData, error: raffleError } = await supabase
        .from('raffles')
        .select('id, estado, boletos_totales')
        .eq('estado', 'activo')
        .single();

      if (raffleError) {
        console.error("❌ Error en la consulta a raffles:", raffleError);
        throw raffleError;
      }

      if (!raffleData) {
         console.warn("⚠️ La consulta a raffles devolvió null o undefined.");
         setError("No hay un sorteo activo.");
         setAvailableTickets([]);
         return;
      }

      console.log("✅ Sorteo activo encontrado:", raffleData.id, "con estado:", raffleData.estado, "y boletos_totales:", raffleData.boletos_totales);

      const { data: ticketsData, error: ticketsError } = await supabase
        .from('tickets')
        .select('numero, raffle_id, estado')
        .match({ raffle_id: raffleData.id, estado: 'disponible' })
        .order('numero')
        .range(0, 99999);

      if (ticketsError) {
        console.error("❌ Error en la consulta a tickets:", ticketsError);
        throw ticketsError;
      }

      if (!ticketsData || ticketsData.length === 0) {
        console.warn("⚠️ No se encontraron tickets disponibles para el sorteo activo.");
        
        const totalTickets = Number(raffleData.boletos_totales);
        if (!totalTickets || typeof totalTickets !== 'number' || totalTickets <= 0) {
          console.error("❌ El sorteo activo no tiene un valor válido para 'boletos_totales'.");
          setError("Error: Configuración inválida del sorteo (boletos_totales).");
          setAvailableTickets([]);
          return;
        }

        const numbersToInsert = Array.from({ length: totalTickets }, (_, i) => ({
          numero: (i + 1).toString().padStart(3, '0'),
          raffle_id: raffleData.id,
          estado: 'disponible'
        }));

        const { data: insertedTickets, error: insertError } = await supabase
          .from('tickets')
          .insert(numbersToInsert)
          .select('numero');

        if (insertError) {
          console.error("❌ Error al insertar los tickets:", insertError);
          setError("Error al generar los tickets. Por favor, inténtalo de nuevo.");
          setAvailableTickets([]);
          return;
        }

        const ticketNumbers = (insertedTickets && insertedTickets.length > 0)
          ? insertedTickets.map(t => Number(t.numero))
          : numbersToInsert.map(t => Number(t.numero));

        console.log(`✅ Tickets disponibles obtenidos: ${ticketNumbers.length}`);
        setAvailableTickets(ticketNumbers);
        return;
      }

      const ticketNumbers = ticketsData.map(ticket => {
        const numero = Number(ticket.numero);
        if (isNaN(numero)) {
          console.warn("⚠️ Ticket con numero no numérico:", ticket);
        }
        return numero;
      }).filter(num => !isNaN(num));

      console.log(`✅ Tickets disponibles obtenidos: ${ticketNumbers.length}`);
      setAvailableTickets(ticketNumbers);

    } catch (err) {
      console.error('🚨 Error al cargar los boletos disponibles:', err);
      setError(err.message || 'Error desconocido');
      setAvailableTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const checkTicketAvailability = useCallback(async (ticketNumber) => {
    const cacheKey = ticketNumber.toString();
    
    if (verificationCache.has(cacheKey)) {
      return verificationCache.get(cacheKey);
    }

    try {
      const { data: raffleData, error: raffleError } = await supabase
        .from('raffles')
        .select('id')
        .eq('estado', 'activo')
        .single();

      if (raffleError || !raffleData) {
        const result = { available: false, error: true };
        setVerificationCache(prev => new Map(prev).set(cacheKey, result));
        return result;
      }

      const { data: sold, error: soldError } = await supabase
        .from('tickets')
        .select('numero')
        .eq('numero', ticketNumber)
        .eq('raffle_id', raffleData.id)
        .neq('estado', 'disponible');

      if (soldError) {
        const result = { available: false, error: true };
        setVerificationCache(prev => new Map(prev).set(cacheKey, result));
        return result;
      }

      const result = { available: sold?.length === 0, error: false };
      setVerificationCache(prev => new Map(prev).set(cacheKey, result));
      return result;

    } catch (err) {
      const result = { available: false, error: true };
      setVerificationCache(prev => new Map(prev).set(cacheKey, result));
      return result;
    }
  }, [verificationCache]);

  const verifyMultipleTickets = useCallback(async (ticketNumbers) => {
    const newCache = new Map(verificationCache);
    const toVerify = [];

    for (const ticketNumber of ticketNumbers) {
      const cacheKey = ticketNumber.toString();
      if (!verificationCache.has(cacheKey)) {
        toVerify.push(ticketNumber);
        setVerifyingTickets(prev => new Set(prev).add(ticketNumber));
      }
    }

    if (toVerify.length === 0) {
      setVerifyingTickets(new Set());
      return;
    }

    try {
      const { data: raffleData, error: raffleError } = await supabase
        .from('raffles')
        .select('id')
        .eq('estado', 'activo')
        .single();

      if (raffleError || !raffleData) {
        throw new Error("No se pudo obtener el sorteo activo");
      }

      const { data: soldTickets, error: soldError } = await supabase
        .from('tickets')
        .select('numero')
        .eq('raffle_id', raffleData.id)
        .neq('estado', 'disponible')
        .in('numero', toVerify.map(t => t.toString()));

      if (soldError) {
        throw new Error("Error al verificar tickets");
      }

      const soldNumbers = new Set(soldTickets?.map(t => Number(t.numero)) || []);
      
      toVerify.forEach(ticketNumber => {
        newCache.set(ticketNumber.toString(), {
          available: !soldNumbers.has(ticketNumber),
          error: false
        });
      });

      setVerificationCache(newCache);

    } catch (err) {
      console.error("Error en verificación masiva:", err);
      toVerify.forEach(ticketNumber => {
        newCache.set(ticketNumber.toString(), {
          available: false,
          error: true
        });
      });
      setVerificationCache(newCache);
    } finally {
      toVerify.forEach(ticketNumber => {
        setVerifyingTickets(prev => {
          const newSet = new Set(prev);
          newSet.delete(ticketNumber);
          return newSet;
        });
      });
    }
  }, [verificationCache]);

  useEffect(() => {
    fetchAvailableTickets();
  }, []);

  useEffect(() => {
    setQuantity(quantityFromParent || 2);
  }, [quantityFromParent]);

  useEffect(() => {
    if (selectionMode === 'manual') {
      const newQuantity = selectedTickets.size;
      onQuantityChange(newQuantity);
    }
  }, [selectedTickets, selectionMode, onQuantityChange]);

  useEffect(() => {
    if (selectionMode === 'manual') {
      onTicketsSelected(selectedTickets);
    } else {
      onTicketsSelected(new Set());
    }
  }, [selectedTickets, selectionMode, onTicketsSelected]);

  useEffect(() => {
    if (selectionMode === 'manual') {
      const newSelections = Array.from(selectedTickets).filter(ticket => 
        !verificationCache.has(ticket.toString())
      );
      if (newSelections.length > 0) {
        verifyMultipleTickets(newSelections);
      }
    }
  }, [selectedTickets, selectionMode, verifyMultipleTickets, verificationCache]);

  // Filtrar boletos por término de búsqueda
  const filteredTickets = useMemo(() => {
    return availableTickets.filter(ticket =>
      ticket.toString().includes(searchTerm)
    );
  }, [availableTickets, searchTerm]);

  // ✅ RESPONSIVIDAD CORREGIDA: 3 columnas en móvil, 6 en escritorio
  const gridConfig = useMemo(() => {
    const containerWidth = gridWidth || 800;
    let columnCount;
    
    // Reglas simples y directas
    if (containerWidth < 640) {
      columnCount = 3;  // 📱 Móvil: 3 columnas
    } else {
      columnCount = 6;  // 💻 Escritorio/Horizontal: 6 columnas
    }

    const columnWidth = Math.floor(containerWidth / columnCount);
    const rowCount = Math.ceil(filteredTickets.length / columnCount);

    console.log(`📐 Grid Config: containerWidth=${containerWidth}px, columnCount=${columnCount}`);

    return { columnCount, columnWidth, rowCount, rowHeight: 80 };
  }, [gridWidth, filteredTickets.length]);

  const handleIncrement = () => {
    if (!disabled) {
      const newQuantity = quantity + 1;
      setQuantity(newQuantity);
      onQuantityChange(newQuantity);
    }
  };

  const handleDecrement = () => {
    if (quantity > 2 && !disabled) {
      const newQuantity = quantity - 1;
      setQuantity(newQuantity);
      onQuantityChange(newQuantity);
    }
  };

  const handlePlusMouseDown = () => {
    if (!disabled) setActiveButton('plus');
  };

  const handleMinusMouseDown = () => {
    if (quantity > 2 && !disabled) setActiveButton('minus');
  };

  const handleMouseUp = () => {
    setActiveButton(null);
  };

  const handleTouchStartPlus = (e) => {
    e.preventDefault();
    handlePlusMouseDown();
  };

  const handleTouchStartMinus = (e) => {
    e.preventDefault();
    handleMinusMouseDown();
  };

  const handleTicketSelect = async (ticketNumber) => {
    if (disabled) return;

    const isCurrentlySelected = selectedTickets.has(ticketNumber);
    const cachedResult = verificationCache.get(ticketNumber.toString());
    
    if (isCurrentlySelected) {
      const newSelected = new Set(selectedTickets);
      newSelected.delete(ticketNumber);
      setSelectedTickets(newSelected);
    } else {
      if (!cachedResult) {
        setVerifyingTickets(prev => new Set(prev).add(ticketNumber));
        const availabilityResult = await checkTicketAvailability(ticketNumber);
        setVerifyingTickets(prev => {
          const newSet = new Set(prev);
          newSet.delete(ticketNumber);
          return newSet;
        });

        if (!availabilityResult.available) {
          alert("¡Este boleto ya no está disponible! Recarga la página para ver los disponibles.");
          await fetchAvailableTickets();
          return;
        }
      } else if (!cachedResult.available) {
        alert("¡Este boleto ya no está disponible! Recarga la página para ver los disponibles.");
        await fetchAvailableTickets();
        return;
      }

      const newSelected = new Set(selectedTickets);
      newSelected.add(ticketNumber);
      setSelectedTickets(newSelected);
    }
  };

  const handleDeselectAll = () => {
    if (!disabled) {
      setSelectedTickets(new Set());
    }
  };

  const totalPrice = (selectionMode === 'random' ? quantity : selectedTickets.size) * ticketPrice;

  useImperativeHandle(ref, () => ({
    refreshTickets: fetchAvailableTickets,
    deselectAll: handleDeselectAll,
    getSelectedTickets: () => new Set(selectedTickets)
  }));

  // Componente para renderizar cada celda del grid (memoizado para mejor rendimiento)
  const Cell = React.memo(({ columnIndex, rowIndex, style }) => {
    const index = rowIndex * gridConfig.columnCount + columnIndex;
    if (index >= filteredTickets.length) return null;

    const num = filteredTickets[index];
    const isSelected = selectedTickets.has(num);
    const isVerifying = verifyingTickets.has(num);
    const isDisabled = disabled;
    const cachedResult = verificationCache.get(num.toString());
    const isUnavailable = cachedResult && !cachedResult.available;

    return (
      <div style={{...style, padding: '6px'}}>
        <button
          type="button"
          onClick={() => !isDisabled && !disabled && !isUnavailable && handleTicketSelect(num)}
          disabled={disabled || isDisabled || isUnavailable}
          className={`
            relative w-full h-16 flex rounded-xl shadow-sm cursor-pointer transition-all duration-300 ease-in-out transform hover:scale-105
            ${isSelected
              ? 'bg-gradient-to-r from-yellow-200 to-yellow-400 border-2 border-secondary shadow-golden scale-105'
              : isUnavailable
                ? 'bg-gray-200 border border-gray-400 opacity-60 cursor-not-allowed'
                : 'bg-white hover:bg-gray-50 border border-border'
            }
            ${isVerifying ? 'animate-pulse' : ''}
            ${disabled || isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {isVerifying && (
            <div className="absolute top-1 right-1 w-3 h-3 bg-blue-500 rounded-full animate-ping"></div>
          )}
          
          {isUnavailable && (
            <div className="absolute top-1 right-1">
              <Icon name="X" size={12} className="text-red-500" />
            </div>
          )}

          <div className="flex flex-col w-full h-full p-1 justify-between">
            <div className="flex items-center justify-center space-x-1 text-[10px] font-bold text-gray-600">
              <span>☆</span>
              <span>LOTO</span>
              <span>☆</span>
            </div>
            <div className="w-full border-t border-dashed border-gray-300 my-0.5"></div>
            <div className="flex items-center justify-center flex-grow">
              <span className={`font-mono font-extrabold text-lg leading-none ${
                isSelected ? 'text-amber-900' : isUnavailable ? 'text-gray-500' : 'text-gray-800'
              }`}>
                {num.toString().padStart(4, '0')}
              </span>
            </div>
          </div>
        </button>
      </div>
    );
  });

  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border p-6 shadow-luxury flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-secondary mb-4"></div>
        <p className="text-lg">Cargando boletos disponibles...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-xl border border-border p-6 shadow-luxury flex flex-col items-center justify-center h-64">
        <Icon name="AlertCircle" size={48} className="text-error mb-4" />
        <h3 className="text-lg font-heading font-semibold text-error mb-2">
          Error al cargar los boletos
        </h3>
        <p className="text-sm text-gray-800 font-caption text-center mb-4">
          {error}
        </p>
        <Button
          variant="outline"
          onClick={() => window.location.reload()}
        >
          Reintentar
        </Button>
      </div>
    );
  }

  if (availableTickets.length === 0 && !loading) {
    return (
      <div className="bg-card rounded-xl border border-border p-6 shadow-luxury flex flex-col items-center justify-center h-64">
        <Icon name="Ticket" size={48} className="text-muted-foreground mb-4" />
        <h3 className="text-lg font-heading font-semibold text-gray-800 mb-2">
          No hay boletos disponibles
        </h3>
        <p className="text-sm text-gray-800 font-caption text-center">
          Por favor, inténtalo de nuevo más tarde o contacta al soporte.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-luxury">
      <div className="flex border-b border-border mb-4">
        <button
          className={`py-2 px-4 font-medium text-sm ${
            selectionMode === 'manual'
              ? 'text-secondary border-b-2 border-secondary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setSelectionMode('manual')}
          disabled={disabled}
        >
          Selección Manual
        </button>
        <button
          className={`py-2 px-4 font-medium text-sm ${
            selectionMode === 'random'
              ? 'text-secondary border-b-2 border-secondary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setSelectionMode('random')}
          disabled={disabled}
        >
          Selección Aleatoria
        </button>
      </div>

      {selectionMode === 'manual' && (
        <>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-heading font-semibold text-gray-800">
              Seleccionar Boletos
            </h3>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <Icon name="Ticket" size={16} className="text-secondary" />
                <span className="text-sm text-gray-800 font-caption">
                  Seleccionados: {selectedTickets.size}
                </span>
              </div>
              {selectedTickets.size > 0 && (
                <button
                  onClick={handleDeselectAll}
                  className="text-xs text-red-600 hover:text-red-800 transition-colors"
                  disabled={disabled}
                >
                  Limpiar todo
                </button>
              )}
            </div>
          </div>

          <div className="relative mb-4">
            <input
              type="text"
              placeholder="Buscar número de boleto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
            />
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>

          <div className="flex justify-between items-center mb-4">
            <span className="text-xs text-gray-800">
              Mostrando {filteredTickets.length.toLocaleString()} de {availableTickets.length.toLocaleString()} boletos disponibles
            </span>
          </div>

          <div 
            className="bg-card rounded-lg p-4 md:p-6 mb-6"
            ref={(el) => {
              if (el && el.offsetWidth !== gridWidth) {
                setGridWidth(el.offsetWidth - 48); // Restar padding
              }
            }}
          >
            {gridWidth > 0 && (
              <Grid
                columnCount={gridConfig.columnCount}
                columnWidth={gridConfig.columnWidth}
                height={600}
                rowCount={gridConfig.rowCount}
                rowHeight={gridConfig.rowHeight}
                width={gridWidth}
                className="scrollbar-thin scrollbar-thumb-secondary scrollbar-track-muted"
              >
                {Cell}
              </Grid>
            )}
          </div>
        </>
      )}

      {selectionMode === 'random' && (
        <>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-heading font-semibold text-gray-800">
              Seleccionar Cantidad
            </h3>
            <div className="flex items-center space-x-1">
              <Icon name="Ticket" size={16} className="text-secondary" />
              <span className="text-sm text-gray-800 font-caption">
                Mín. 2 boletos
              </span>
            </div>
          </div>
          <div className="flex items-center justify-center space-x-4 mb-6">
            <button
              type="button"
              onClick={handleDecrement}
              disabled={quantity <= 2 || disabled}
              onMouseDown={handleMinusMouseDown}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStartMinus}
              onTouchEnd={handleMouseUp}
              className={`
                w-12 h-12 rounded-full flex items-center justify-center
                border border-gray-300
                transition-colors duration-75 ease-in-out
                ${disabled 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : activeButton === 'minus'
                    ? 'bg-red-500 text-white border-red-600'
                    : 'bg-white text-gray-800 hover:bg-gray-50'
                }
              `}
            >
              <Icon name="Minus" size={20} />
            </button>

            <div className="text-center">
              <div className="text-4xl font-mono font-bold text-gray-800 mb-1">
                {quantity}
              </div>
              <div className="text-sm text-gray-800 font-caption">
                {quantity === 1 ? 'boleto' : 'boletos'}
              </div>
            </div>

            <button
              type="button"
              onClick={handleIncrement}
              disabled={disabled}
              onMouseDown={handlePlusMouseDown}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStartPlus}
              onTouchEnd={handleMouseUp}
              className={`
                w-12 h-12 rounded-full flex items-center justify-center
                border border-gray-300
                transition-colors duration-75 ease-in-out
                ${disabled 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : activeButton === 'plus'
                    ? 'bg-green-500 text-white border-green-600'
                    : 'bg-white text-gray-800 hover:bg-gray-50'
                }
              `}
            >
              <Icon name="Plus" size={20} />
            </button>
          </div>
        </>
      )}

      <div className="mt-4 bg-muted/30 rounded-lg p-4 border border-golden/20">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-800 font-caption">
            Precio por boleto:
          </span>
          <span className="font-mono text-gray-800">
            Bs. {ticketPrice?.toLocaleString()}
          </span>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="font-body font-medium text-gray-800">
            Total a pagar:
          </span>
          <div className="text-right">
            <div className="text-xl font-mono font-bold text-secondary">
              Bs. {totalPrice?.toLocaleString()}
            </div>
            <div className="text-xs text-gray-800 font-caption">
              {selectionMode === 'random' ? quantity : selectedTickets.size} × Bs. {ticketPrice?.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {(selectionMode === 'random' && quantity <= 2) || 
       (selectionMode === 'manual' && selectedTickets.size <= 2) ? (
        <div className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-warning" />
            <span className="text-sm text-warning font-caption">
              Mínimo 2 boletos por compra
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
});

export default QuantitySelector;
